import Keycloak from "keycloak-js/dist/keycloak.js";

import { config } from "@/config";

const STORAGE_PREFIX = "iwayKeycloak";
const SESSION_KEY = STORAGE_PREFIX + "Session";
const NEXT_KEY = STORAGE_PREFIX + "Next";

let keycloak = null;
let initialization = null;
let refresh = null;
let sessionGeneration = 0;

function enabled() {
  return (
    config.mandant === "iway" &&
    (config.keycloakEnabled === true || config.keycloakEnabled === "true")
  );
}

function redirectUri() {
  return window.location.origin + "/sso-callback";
}

function persistSession() {
  if (!keycloak || !keycloak.authenticated) return;
  sessionStorage.setItem(SESSION_KEY, "true");
}

function clearStoredSession(clearNext = true) {
  sessionGeneration += 1;
  sessionStorage.removeItem(SESSION_KEY);
  if (clearNext) sessionStorage.removeItem(NEXT_KEY);
}

function hasStoredSession() {
  return enabled() && sessionStorage.getItem(SESSION_KEY) === "true";
}

function initialize() {
  if (!enabled()) return Promise.resolve(false);
  if (initialization) return initialization;

  keycloak = new Keycloak({
    url: config.keycloakUrl,
    realm: config.keycloakRealm,
    clientId: config.keycloakClientId,
  });

  const initOptions = {
    checkLoginIframe: false,
    pkceMethod: "S256",
    redirectUri: redirectUri(),
    responseMode: "fragment",
  };

  if (
    hasStoredSession() &&
    window.location.pathname !== "/sso-callback"
  )
    initOptions.onLoad = "check-sso";

  initialization = keycloak
    .init(initOptions)
    .then((authenticated) => {
      if (authenticated) persistSession();
      else clearStoredSession();
      return authenticated;
    })
    .catch((error) => {
      clearStoredSession(false);
      initialization = null;
      throw error;
    });

  return initialization;
}

async function login(next) {
  await initialize();
  if (next && next !== "/login" && next !== "/sso-callback")
    sessionStorage.setItem(NEXT_KEY, next);
  else sessionStorage.removeItem(NEXT_KEY);
  sessionStorage.setItem(SESSION_KEY, "true");

  return keycloak.login({
    redirectUri: redirectUri(),
  });
}

async function getValidToken() {
  const authenticated = await initialize();
  if (!authenticated) throw new Error("Keycloak session is not authenticated");

  if (!refresh) {
    const generation = sessionGeneration;
    refresh = keycloak
      .updateToken(30)
      .then(() => {
        if (generation !== sessionGeneration)
          throw new Error("Keycloak session was cleared while refreshing");
        persistSession();
        return keycloak.token;
      })
      .finally(() => {
        refresh = null;
      });
  }

  return refresh;
}

async function logout() {
  clearStoredSession();
  keycloak = null;
  initialization = null;
  refresh = null;
  window.location.replace(window.location.origin + "/login");
}

function consumeNext() {
  const next = sessionStorage.getItem(NEXT_KEY);
  sessionStorage.removeItem(NEXT_KEY);
  return next;
}

function pendingNext() {
  return sessionStorage.getItem(NEXT_KEY);
}

function setPendingNext(next) {
  if (next) sessionStorage.setItem(NEXT_KEY, next);
  else sessionStorage.removeItem(NEXT_KEY);
}

function currentToken() {
  return keycloak && keycloak.authenticated ? keycloak.token : null;
}

export {
  clearStoredSession,
  consumeNext,
  currentToken,
  enabled,
  getValidToken,
  hasStoredSession,
  initialize,
  login,
  logout,
  pendingNext,
  setPendingNext,
};
